import React, { useState } from "react";
import {
  IconButton,
  Badge,
  Popover,
  List,
  Typography,
  Divider,
  Button,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useMessages } from "../../context/MessagesContext";
import { Notification } from "../../interfaces";
import NotificationItem from "./NotificationItem";
import { useNavigate } from "react-router-dom";
import { useTimezone } from "../../context/TimezoneContext";
import moment from "moment-timezone";

const NotificationList: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead } = useMessages();
  const { timezone } = useTimezone();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filter, setFilter] = useState<string>("all");
  const navigate = useNavigate();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const viewAll = () => navigate(`/notifications`);

  const unreadCount = notifications.filter(
    (n: Notification) => !n.is_read
  ).length;
  const FILTERS = [
    { value: "all", label: "All" },
    { value: "info", label: "Info" },
    { value: "success", label: "Success" },
    { value: "warning", label: "Warning" },
    { value: "error", label: "Error" },
  ];

  const filteredNotifications =
    filter === "all"
      ? notifications
      : notifications.filter((notif) => notif.category === filter);

  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    if (a.is_read === b.is_read) {
      const timeA = moment.utc(a.created_at).tz(timezone).valueOf();
      const timeB = moment.utc(b.created_at).tz(timezone).valueOf();
      return timeB - timeA;
    }
    return a.is_read ? 1 : -1;
  });

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        size="small"
        sx={{
          height: { xs: 36, sm: 40 },
          width: { xs: 36, sm: 40 },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              fontWeight: 800,
              height: 18,
              minWidth: 18,
              right: 3,
              top: 4,
            },
          }}
        >
          <NotificationsIcon sx={{ color: "primary.main" }} />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: "calc(100vw - 24px)", sm: 400 },
            maxHeight: { xs: "calc(100vh - 88px)", sm: 600 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2.5,
            pt: 2,
            pb: 1.5,
          }}
        >
          <Typography sx={{ flexGrow: 1, fontSize: "1.05rem", fontWeight: 800 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              size="small"
              label={`${unreadCount} new`}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
          )}
          <Button
            onClick={markAllAsRead}
            size="small"
            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
            disabled={unreadCount === 0}
            sx={{ minWidth: 0 }}
          >
            Read all
          </Button>
        </Box>

        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
            {FILTERS.map((f) => (
              <Chip
                key={f.value}
                label={f.label}
                size="small"
                onClick={() => setFilter(f.value)}
                variant={filter === f.value ? "filled" : "outlined"}
                color={filter === f.value ? "primary" : "default"}
                sx={{ fontWeight: 600, cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <List disablePadding>
            {sortedNotifications.length > 0 ? (
              sortedNotifications.map((notification, idx) => (
                <React.Fragment key={`${notification.id}-${timezone}`}>
                  {idx > 0 && <Divider component="li" />}
                  <NotificationItem
                    notification={notification}
                    markAsRead={markAsRead}
                  />
                </React.Fragment>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  color: "text.secondary",
                  py: 5,
                }}
              >
                <NotificationsNoneIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                <Typography sx={{ fontSize: "0.9rem" }}>
                  You're all caught up
                </Typography>
              </Box>
            )}
          </List>
        </Box>

        <Divider />

        <Box sx={{ p: 1.25 }}>
          <Button
            fullWidth
            variant="text"
            color="primary"
            onClick={viewAll}
            sx={{ fontWeight: 700 }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationList;
